import React, { useCallback, useEffect, useMemo } from 'react';
import { styled } from 'styled-components';
import { Play, PlayFolder } from '../types';
import { PoolElement } from '../Pool';
import {
  paginatorInactive,
  darkBlueBackground,
  darkBlueBorder,
  lighterBlueBackground,
  lighterBlueBorder,
  red,
  white,
  yellow,
} from './colors';

import {
  PlusIcon,
  XCircleIcon,
  TrashIcon,
  FolderPlusIcon,
  FolderArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { CreateFolderModal } from './plays/CreateFolderModal';
import {
  $playFolderAssignModalIsOpen,
  $playFolderModalIsOpen,
  $playFolders,
  $playFolderSelectModalIsOpen,
  getFolders,
} from '../store/playFolders';
import { SelectFolderModal } from './plays/SelectFolderModal';
import { AssignFolderModal } from './plays/AssignFolderModal';
import { useStore } from '@nanostores/react';
import { getPlays } from '../store/plays';

const Panel = styled.div<{ $isOpen?: boolean }>`
  height: 92dvh;
  display: ${(props) => (props.$isOpen ? 'flex' : 'none')};
  margin: 4dvh 4dvw;
  font-size: 1rem;
  flex-direction: column;
  font-weight: bold;
  width: 85dvw;
`;
const PanelContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 0;
  overflow-y: hidden;
  height: 100%;
`;

const Folders = styled.div`
  display: flex;
  flex-direction: row;
  text-transform: uppercase;
  font-size: 1.5rem;
  cursor: pointer;
  font-weight: bold;
  user-select: none;
`;

const SearchBox = styled.input`
  width: 33dvw;
  background-color: transparent;
  color: ${white};
  padding: 0.3rem 0.5rem;
  border-color: ${white};
  border-style: solid;
  border-width: 0.2rem;
  border-radius: 0.5rem;
  text-transform: uppercase;
  font-size: 1.5rem;
  font-weight: bold;
  outline: none;
  height: 2.9rem;
  box-sizing: border-box;

  &::placeholder {
    color: ${white};
  }
`;

const SearchBoxContainer = styled.div`
  display: block;
  position: relative;
`;

const SearchBoxClearButton = styled.button`
  width: calc(40px + 0.5rem);
  height: 40px;
  background: transparent;
  border-radius: 0.5rem;
  color: #fff;
  position: absolute;
  right: 0;
  padding-right: 0.5rem;
  padding-left: 0;
  top: 0.18rem;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
`;

const Folder = styled.div<{ $selected: boolean }>`
  color: ${white};
  background: ${(props) => (props.$selected ? white : 'none')};
  color: ${(props) => (props.$selected ? '#26386b' : white)};
  flex-shrink: 0;
  padding: 0.3rem 0.5rem;
  border-color: ${white};
  border-style: solid;
  border-left-width: 0rem;
  border-right-width: 0rem;
  border-top-width: 0.2rem;
  border-bottom-width: 0.2rem;
  user-select: none;
  box-sizing: border-box;
  height: 2.9rem;

  &:first-child {
    border-top-left-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;
    border-left-width: 0.2rem;
  }
  &:last-child {
    border-top-right-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    border-right-width: 0.2rem;
  }
`;
const PlayContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 1.3rem;
  cursor: pointer;
  align-items: center;
  background-image: linear-gradient(
    to bottom right,
    ${lighterBlueBackground},
    ${darkBlueBackground}
  );
  padding: 1rem;
`;

const PlayOuterContainer = styled.div<{
  $onlySelect?: boolean;
  $isSelected?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-items: center;
  border-radius: 1.5rem;
  background-image: linear-gradient(
    to bottom right,
    ${lighterBlueBorder},
    ${darkBlueBorder}
  );
  padding: 0.2rem;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  ${(props) =>
    props.$isSelected &&
    `
    box-shadow: 0 0 0 3px ${yellow};
  `}

  ${(props) =>
    props.$onlySelect &&
    `
    &:hover {
      transform: scale(1.03);
      box-shadow: 0 0 0 2px ${white};
    }
  `}

  ${(props) =>
    props.$onlySelect &&
    props.$isSelected &&
    `
    box-shadow: 0 0 0 3px ${yellow};
    &:hover {
      box-shadow: 0 0 0 3px ${yellow};
    }
  `}
`;
const PlayTitle = styled.div`
  text-align: center;
  background-color: ${white};
  color: #26386b;
  font-size: 0.7rem;
  padding: 0.1rem 0.3rem;
  border-radius: 0.2rem;
  text-transform: uppercase;
  position: relative;
  margin-top: -0.65rem;
  z-index: 35;
`;
const PlayPreview = styled.div<{ $colorMode: 'light' | 'dark' }>`
  position: relative;
  border-radius: 8px;
  display: flex;
  justify-content: center;
`;

const PlayView = styled.div`
  display: flex;
  overflow-y: auto;
  scrollbar-width: thin;
  overscroll-behavior: contain;
  flex-wrap: wrap;
  color: ${white};
  align-items: center;
  justify-content: space-evenly;
  gap: 3rem;
  padding-bottom: 50px;
`;

const PlayFilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 2rem;
  gap: 36px;
  justify-content: space-between;
  width: 100%;
  margin: 0;
`;

const isMatchingSearch = (text: string, searchText: string) => {
  if (searchText === '') {
    return true;
  }
  const tidyAccents = (str: string) => {
    return str
      .toLowerCase()
      .replace(new RegExp(/\s/g), '')
      .replace(new RegExp(/[àáâãäå]/g), 'a')
      .replace(new RegExp(/æ/g), 'ae')
      .replace(new RegExp(/ç/g), 'c')
      .replace(new RegExp(/[èéêë]/g), 'e')
      .replace(new RegExp(/[ìíîï]/g), 'i')
      .replace(new RegExp(/ñ/g), 'n')
      .replace(new RegExp(/[òóôõő]/g), 'o')
      .replace(new RegExp(/œ/g), 'oe')
      .replace(new RegExp(/[ùúûüű]/g), 'u')
      .replace(new RegExp(/[ýÿ]/g), 'y')
      .replace(new RegExp(/\W/g), '');
  };
  return tidyAccents(text).includes(tidyAccents(searchText));
};

export const PlayBrowser: React.FC<{
  colorMode?: 'light' | 'dark';
  isOpen?: boolean;
  plays: Record<string, Play[]>;
  onSelectPlay?: (formationId: string) => void;
  onDeletePlay?: (play: Play) => void;
  onAddPlay?: (selectedFolder: string) => void;
  poolMaxHeight?: number;
  isPro?: boolean;
  onlySelect?: boolean;
  selectedPlayId?: string;
}> = ({
  isOpen = false,
  plays,
  poolMaxHeight = 0,
  onSelectPlay,
  onDeletePlay,
  onAddPlay,
  colorMode = 'light',
  isPro = false,
  onlySelect = false,
  selectedPlayId,
}) => {
  const [selectedFolder, setSelectedFolder] = React.useState<string | null>(
    null,
  );
  const [searchText, setSearchText] = React.useState<string>('');
  const [page, setPage] = React.useState<number>(1);
  const [selectedPlayFolder, setSelectedPlayFolder] = React.useState<
    Pick<PlayFolder, 'id' | 'name'>
  >({ id: 'default', name: 'Default' });
  const playFolderList = useStore($playFolders);

  useEffect(() => {
    const getPlayFolders = async () => {
      await getFolders();
      await getPlays();
    };

    if (window.localStorage && window.localStorage.getItem('playFolderId')) {
      const lastSelectedFolderCategory =
        window.localStorage.getItem('lastSelectedFolder');
      if (lastSelectedFolderCategory === 'Custom') {
        setSelectedPlayFolder({
          id: window.localStorage.getItem('playFolderId') || 'default',
          name: window.localStorage.getItem('playFolderName') || 'Default',
        });
      }
    }

    void getPlayFolders();
  }, []);

  const selectedPlayFolderName = useMemo(() => {
    if (playFolderList && playFolderList.length > 0) {
      const found = playFolderList.find(
        (folder) => folder.id === selectedPlayFolder.id,
      );
      return found
        ? found.name
        : selectedPlayFolder.id === 'all'
          ? 'All plays'
          : 'Default';
    }
    return selectedPlayFolder.id === 'all' ? 'All plays' : 'Default';
  }, [selectedPlayFolder, playFolderList]);

  useEffect(() => {
    if (
      selectedPlayFolder.id === 'default' ||
      selectedPlayFolder.id === 'all'
    ) {
      return;
    }
    if (playFolderList && playFolderList.length > 0) {
      const found = playFolderList.find(
        (folder) => folder.id === selectedPlayFolder.id,
      );
      if (!found) {
        setSelectedPlayFolder({ id: 'default', name: 'Default' });
      }
    } else {
      setSelectedPlayFolder({ id: 'default', name: 'Default' });
    }
  }, [selectedPlayFolder, playFolderList]);

  const filteredPlays = useMemo(() => {
    const pageLimit = 10;
    if (!selectedFolder || !plays[selectedFolder]) {
      return [];
    }
    const nameFilteredPlays = plays[selectedFolder].filter((formation) =>
      searchText && searchText !== ''
        ? isMatchingSearch(formation.name, searchText)
        : true,
    );
    // Szétválogatjuk a playeket oldalak szerint, Record<string, Play[]>
    const playsByPage: Record<number, Play[]> = {};

    nameFilteredPlays
      .filter((play) =>
        selectedFolder === 'Custom'
          ? selectedPlayFolder.id === 'all'
            ? true
            : selectedPlayFolder.id === play.folderId ||
              (selectedPlayFolder.id === 'default' && !play.folderId)
          : true,
      )
      .forEach((play, index) => {
        const pageIndex = Math.floor(index / pageLimit) + 1;
        if (!playsByPage[pageIndex]) {
          playsByPage[pageIndex] = [];
        }
        playsByPage[pageIndex].push(play);
      });
    return playsByPage;
  }, [plays, selectedFolder, searchText, page, selectedPlayFolder]);

  const pages = useMemo(() => {
    return Object.keys(filteredPlays).map((page) => parseInt(page, 10));
  }, [filteredPlays]);

  const openFolderAssignModal = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    play: Play,
  ) => {
    e.stopPropagation();
    e.preventDefault();

    $playFolderAssignModalIsOpen.set({ play, isOpen: true });
  };
  const _onDeletePlay = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    play: Play,
  ) => {
    e.stopPropagation();
    e.preventDefault();

    if (onDeletePlay) {
      onDeletePlay(play);
    }
  };

  useEffect(() => {
    if (selectedFolder === null) {
      const lastSelectedFolder =
        window.localStorage.getItem('lastSelectedFolder');
      if (
        lastSelectedFolder &&
        Object.keys(plays).includes(lastSelectedFolder)
      ) {
        setSelectedFolder(lastSelectedFolder);
        return;
      }
      const group = Object.keys(plays)[0];
      setSelectedFolder(group);
    }
    setPage(1);
  }, [plays, selectedFolder, selectedPlayFolder]);

  const openCreateFolderModal = useCallback(() => {
    if (onlySelect) return;
    $playFolderModalIsOpen.set({ isOpen: true });
  }, [onlySelect]);

  const openSelectFolderModal = useCallback(() => {
    $playFolderSelectModalIsOpen.set(true);
  }, []);

  const onSelectFolderCategory = useCallback((folder: string) => {
    setSelectedFolder(folder);
    window.localStorage.setItem('lastSelectedFolder', folder);
  }, []);

  const onSelectPlayFolder = useCallback(
    (folder: Pick<PlayFolder, 'id' | 'name'>) => {
      setSelectedPlayFolder(folder);
      window.localStorage.setItem('playFolderId', folder.id!);
      window.localStorage.setItem('playFolderName', folder.name);
      setPage(1);
    },
    [],
  );

  return (
    <>
      <Panel $isOpen={isOpen}>
        <PanelContent>
          {isPro && (
            <>
              <PlayFilterContainer>
                <Folders>
                  {Object.keys(plays).map((folder) => (
                    <Folder
                      $selected={selectedFolder === folder}
                      onClick={() => onSelectFolderCategory(folder)}
                      key={`folder-${folder}`}
                    >
                      {folder}
                    </Folder>
                  ))}
                </Folders>
                <SearchBoxContainer>
                  <SearchBox
                    placeholder="Search for play..."
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setPage(1);
                    }}
                    value={searchText}
                  />
                  {searchText && searchText.length > 0 && (
                    <SearchBoxClearButton
                      onClick={() => {
                        setSearchText('');
                        setPage(1);
                      }}
                    >
                      <XCircleIcon
                        style={{
                          width: '32px',
                          height: '32px',
                          color: '#fff',
                        }}
                      />
                    </SearchBoxClearButton>
                  )}
                </SearchBoxContainer>
              </PlayFilterContainer>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                {isPro && selectedFolder === 'Custom' && (
                  <div
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1.2rem',
                    }}
                  >
                    <div
                      style={{
                        height: '2.7rem',
                        color: '#fff',
                        borderRadius: '12px',
                        border: '2px solid #fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 1rem',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        gap: '10px',
                        width: '200px',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                      }}
                      onClick={openSelectFolderModal}
                    >
                      <div
                        style={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          flexGrow: 1,
                        }}
                      >
                        {selectedPlayFolderName}
                      </div>{' '}
                      <FolderArrowDownIcon
                        style={{ width: '30px', flexShrink: 0 }}
                      />
                    </div>
                    {!onlySelect && (
                      <Button
                        style={{
                          display: 'flex',
                          gap: '10px',
                          height: '2.7rem',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          margin: 0,
                          padding: '0 10px',
                        }}
                        tertiary={true}
                        onClick={() => openCreateFolderModal()}
                      >
                        <FolderPlusIcon style={{ width: '1.8rem' }} />
                      </Button>
                    )}
                  </div>
                )}
                <div
                  className={'pagination'}
                  style={{
                    display: 'flex',
                    flexGrow: '1',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    height: '5rem',
                    flexShrink: 0,
                    gap: '1rem',
                  }}
                >
                  {pages.map((p, index) => (
                    <button
                      key={`page-${p}`}
                      onClick={() => setPage(p)}
                      style={{
                        backgroundColor:
                          page === p ? lighterBlueBackground : 'transparent',
                        border: '2px solid #fff',
                        color: page === p ? white : paginatorInactive,
                        height: '2.7rem',
                        padding: '0 1.3rem',
                        borderRadius: '0.5rem',
                        margin: '0 0.2rem',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1.3rem',
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <PlayView>
            {selectedFolder && selectedFolder !== 'notes' && (
              <>
                {isPro && !onlySelect && (
                  <PlayOuterContainer key={'add'}>
                    <PlayContainer
                      onClick={() => {
                        onAddPlay?.(selectedFolder);
                      }}
                    >
                      <PlayPreview $colorMode={colorMode}>
                        <div
                          style={{
                            height: '197.62845849802372px',
                            width: '237.15415019762844px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: '170px',
                              aspectRatio: '237/197',
                              border: '3px dashed #fff',
                              borderRadius: '1.1rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PlusIcon
                              style={{ maxWidth: '50%', width: '100%' }}
                            />
                          </div>
                        </div>
                      </PlayPreview>
                    </PlayContainer>
                  </PlayOuterContainer>
                )}
                {filteredPlays?.[page]?.map((formation, index) => {
                  return (
                    <PlayOuterContainer
                      key={`play-${formation.id}-${index}`}
                      $onlySelect={onlySelect}
                      $isSelected={
                        onlySelect && selectedPlayId === formation.id
                      }
                    >
                      <PlayContainer
                        onClick={() => {
                          onSelectPlay?.(formation.id || '');
                        }}
                      >
                        {selectedFolder === 'Custom' && !onlySelect && (
                          <>
                            <div
                              onClick={(e) =>
                                openFolderAssignModal(e, formation)
                              }
                              style={{
                                position: 'absolute',
                                bottom: '5px',
                                left: '5px',
                                zIndex: 30,
                                width: '38px',
                                height: '38px',
                                background: darkBlueBorder,
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <FolderPlusIcon
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  color:
                                    selectedPlayFolder.id === 'all' &&
                                    formation.folderId !== null
                                      ? yellow
                                      : white,
                                }}
                              />
                            </div>
                            <div
                              onClick={(e) => _onDeletePlay(e, formation)}
                              style={{
                                position: 'absolute',
                                bottom: '5px',
                                right: '5px',
                                zIndex: 30,
                                width: '38px',
                                height: '38px',
                                background: darkBlueBorder,
                                borderRadius: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <TrashIcon
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  color: red,
                                }}
                              />
                            </div>
                          </>
                        )}
                        <PlayPreview $colorMode={colorMode}>
                          <PoolElement
                            snapshot={formation.snapshots[0]}
                            halfView
                            poolMaxHeight={200}
                            poolMaxWidth={200 * 1.2}
                            isDrawingEnabled={false}
                            shouldCleanDrawings={false}
                            onDrawingsCleaned={() => {}}
                            shouldUndoLastDrawing={false}
                            onLastDrawingUndone={() => {}}
                            opponentIncrease={0}
                            isStatic
                          />
                        </PlayPreview>
                        <PlayTitle>{formation.name}</PlayTitle>
                      </PlayContainer>
                    </PlayOuterContainer>
                  );
                })}
              </>
            )}
          </PlayView>
        </PanelContent>
      </Panel>
      <AssignFolderModal />
      <CreateFolderModal plays={plays['Custom']} />
      <SelectFolderModal
        onSelect={onSelectPlayFolder}
        onlySelect={onlySelect}
      />
    </>
  );
};
